import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import {
  getMeeting,
  updateMeetingInfo,
  addAgendaItem,
  updateAgendaItem,
  deleteAgendaItem,
  updateAgendaOrder,
  addSharedUser,
  removeSharedUser,
  updateMeetingNotes,
  addActionItem,
  updateActionItem,
  updateActionOrder,
  deleteActionItem,
  deleteMeeting,
  duplicateMeeting,
  getPreviousActions,
} from '@/api/meetings';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, Clock, Copy, Trash2, LayoutGrid, List } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmailShare } from '@/components/EmailShare';
import { AgendaList } from '@/components/AgendaList';
import { MeetingEditor } from '@/components/MeetingEditor';
import { ActionList } from '@/components/ActionList';
import { DuplicateMeeting } from '@/components/DuplicateMeeting';
import { useDebounce } from '@/hooks/useDebounce';
import { Meeting, AgendaItem, ActionItem } from '@/types/Meeting';
import { PreviousActionList } from '@/components/PreviousActionList';
import { StructuredError} from '@/types/errors';

interface MeetingDetailProps {
  layout: 'classic' | 'card';
  onToggleLayout: () => void;
}

export function MeetingDetail({ layout, onToggleLayout }: MeetingDetailProps) {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState('12:00');
  const [showCompletedPrevious, setShowCompletedPrevious] = useState(false);
  const { id } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const hasFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    // Prevent duplicate fetches for the same meeting ID
    if (hasFetchedRef.current === id) {
      return;
    }

    const abortController = new AbortController();
    
    const fetchMeeting = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await getMeeting(id!);
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }
        
        const fetchedMeeting = response.meeting;
        
        setMeeting(fetchedMeeting);
        setTitle(fetchedMeeting.title);
        const meetingDate = new Date(fetchedMeeting.date);
        setDate(meetingDate);
        setTime(format(meetingDate, 'HH:mm'));
        
        // Mark this meeting as fetched
        hasFetchedRef.current = id!;
      } catch (error: any) {
        // Don't process errors if request was aborted
        if (abortController.signal.aborted) {
          return;
        }
        
        setError(error.message);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message === 'Access denied'
            ? "You don't have permission to view this meeting"
            : "Failed to load meeting details"
        });
        if (error.message === 'Access denied') {
          navigate('/app/meetings');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
   
    fetchMeeting();
    
    // Cleanup: abort fetch if component unmounts or id changes
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const debouncedTitle = useDebounce(title, 1500);
  useEffect(() => {
    if (debouncedTitle && meeting && debouncedTitle !== meeting.title && title !== meeting.title) {
      updateMeetingInfo(meeting.id, { title: debouncedTitle });
    }
  }, [debouncedTitle, meeting]);

  const handleDeleteMeeting = async () => {
    if (meeting?.id) {
      try {
        await deleteMeeting(meeting.id);
        toast({ title: 'Success', description: 'Meeting deleted successfully' });
        navigate('/app/meetings');
      } catch (error) {
        console.error('Error deleting meeting:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete meeting' });
      }
    }
  };

  interface DuplicateOptions {
    title: string;
    date: Date;
    includeShared: boolean;
    agendaOption: 'all' | 'incomplete';
    includeNotes: boolean;
    actionsOption: 'none' | 'all' | 'move-incomplete';
  }

  interface DuplicateResponse {
    meeting: {
      id: string;
    };
  }

  const handleDuplicate = async (options: DuplicateOptions) => {
    try {
    const response: DuplicateResponse = await duplicateMeeting(meeting!.id, options);
    console.log('Duplicate response:', JSON.stringify(response, null, 2));
    
    if (response.meeting && response.meeting.id) {
        console.log('Navigating to:', response.meeting.id);
        navigate(`/app/meetings/${response.meeting.id}`);
      } else {
        console.error('No meeting id in response:', response);
        toast({ variant: 'destructive', title: 'Error', description: 'Error accessing new meeting' });
      }
    } catch (error) {
      console.error('Error duplicating meeting:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to duplicate meeting' });
    }
  };

  const handleDateTimeChange = async (newDate: Date | undefined, newTime: string) => {
    if (meeting && newDate) {
      const [hours, minutes] = newTime.split(':').map(Number);
      const updatedDate = new Date(newDate);
      updatedDate.setHours(hours, minutes);
      setDate(updatedDate);
      setTime(newTime);
      setMeeting({ ...meeting, date: updatedDate });

      try {
        await updateMeetingInfo(meeting.id, { date: updatedDate.toISOString() });
        toast({ title: 'Meeting updated', description: 'Date and time updated successfully' });
      } catch (error) {
        console.error('Error updating meeting date/time:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to update date/time' });
      }
    }
  };

  const handleAddSharedUser = async (email: string) => {
    if (meeting && meeting.id) {
      try {
        const response = await addSharedUser(meeting.id, email);
        setMeeting({
          ...meeting,
          sharedWith: [...response.meeting.sharedWith],
        });
        toast({ title: 'User added', description: `${email} has been added successfully.` });
      } catch (error) {
        console.error('Error adding shared user:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to add shared user' });
      }
    }
  };

  const handleRemoveSharedUser = async (email: string) => {
    if (meeting && meeting.id) {
      try {
        await removeSharedUser(meeting.id, email);
        setMeeting({
          ...meeting,
          sharedWith: meeting.sharedWith.filter((sharedEmail) => sharedEmail !== email),
        });
        toast({ title: 'User removed', description: `Removed sharing with ${email}` });
      } catch (error) {
        console.error('Error removing shared user:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to remove shared user' });
      }
    }
  };

  const handleAddAgendaItem = async (newItem: { text: string; completed: boolean, order?: number }) => {
    if (meeting && meeting.id) {
      try {
        const response = await addAgendaItem(meeting.id, newItem);
        setMeeting({
          ...meeting,
          agenda: [...response.meeting.agenda],
        });
      } catch (error) {
        console.error('Error adding agenda item:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to add agenda item' });
      }
    }
  };

  const handleDeleteAgendaItem = async (agendaItemId: string) => {
    if (meeting && meeting.id) {
      try {
        await deleteAgendaItem(meeting.id, agendaItemId);
        setMeeting(prevMeeting => {
          if (!prevMeeting) return prevMeeting;
          return {
            ...prevMeeting,
            agenda: prevMeeting.agenda.filter(item => item._id !== agendaItemId)
          };
        });
      } catch (error) {
        console.error('Error deleting agenda item:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete agenda item' });
      }
    }
  };

  const handleReorderAgenda = async (newAgenda: AgendaItem[]) => {
    if (meeting && meeting.id) {
      console.log("Handle Reordering agenda:", newAgenda);
      const previousAgenda = meeting.agenda;
      setMeeting({ ...meeting, agenda: newAgenda });

      try {
        console.log("Updating agenda as per new order:", newAgenda);
        const updatedAgenda = await updateAgendaOrder(meeting.id, 
          newAgenda.map(item => ({
            ...item,
            order: item.order ?? 0
          }))
        );
        console.log("Updated Agenda from API call:", updatedAgenda);
      } catch (error) {
        console.error('Error updating agenda order:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to update agenda order' });
        setMeeting({ ...meeting, agenda: previousAgenda });
      }
    }
  };

  const handleUpdateAgendaItem = async (agendaItemId: string, updatedItem: { text: string; completed: boolean }) => {
    if (meeting && meeting.id) {
      console.log("Updating agenda item:", agendaItemId, updatedItem, meeting.id);
      try {
        await updateAgendaItem(meeting.id, agendaItemId, updatedItem);
        setMeeting({
          ...meeting,
          agenda: meeting.agenda.map((item) =>
            item._id === agendaItemId ? { ...item, ...updatedItem } : item
          ),
        });
      } catch (error) {
        console.error('Error updating agenda item:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to update agenda item' });
      }
    } else {
      console.error("Meeting or meeting ID is undefined!");
    }
  };

  const handleNotesChange = async (newNotes: string) => {
    if (meeting) {
      try {
        await updateMeetingNotes(meeting.id, newNotes);
        // No need to update state - editor already has current content
        // Updating state causes unnecessary re-renders and interrupts typing
      } catch (error) {
        console.error('Error updating notes:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to update notes' });
      }
    }
  };

  const handleAddActionItem = async (newAction: { text: string; completed: boolean; hashtags?: string[] }) => {
    if (meeting && meeting.id) {
      try {
        const response = await addActionItem(meeting.id, newAction);
        setMeeting({
          ...meeting,
          actions: [...response.meeting.actions],
        });
      } catch (error) {
        console.error('Error adding action item:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to add action item' });
      }
    }
  };

  const handleDeleteActionItem = async (actionItemId: string) => {
    if (meeting && meeting.id) {
      try {
        await deleteActionItem(meeting.id, actionItemId);
        setMeeting(prevMeeting => {
          if (!prevMeeting) return prevMeeting;
          return {
            ...prevMeeting,
            actions: prevMeeting.actions.filter(action => action._id !== actionItemId)
          };
        });
      } catch (error) {
        console.error('Error deleting action item:', error);
        
        // Type guard to check if error is a StructuredError
        if (
          typeof error === 'object' && 
          error !== null && 
          'type' in error && 
          (error as StructuredError).type === 'PERMISSION_DENIED'
        ) {
          toast({ 
            variant: 'destructive', 
            title: 'Permission Denied', 
            description: (error as StructuredError).message || 'You do not have permission to delete this action item.' 
          });
        } else {
          toast({ 
            variant: 'destructive', 
            title: 'Error', 
            description: 'Failed to delete action item' 
          });
        }
      }
    }
  };

  const handleUpdateActionItem = async (actionItemId: string, updatedItem: { text: string; completed: boolean; hashtags?: string[] }) => {
    if (meeting && meeting.id) {
      try {
        const response = await updateActionItem(meeting.id, actionItemId, updatedItem);
        setMeeting({
          ...meeting,
          actions: response.meeting.actions,
        });
      } catch (error) {
        console.error('Error updating action item:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to update action item' });
      }
    }
  };

  const handleReorderAction = async (newActions: ActionItem[]) => {
    const mappedActions = newActions.map(action => ({
      ...action,
      id: action._id,
      order: action.order ?? 0,
    }));
  
    if (meeting && meeting.id) {
      const previousActions = meeting.actions;
      setMeeting({ ...meeting, actions: mappedActions });
  
      try {
        await updateActionOrder(meeting.id, mappedActions);
      } catch (error) {
        console.error('Error updating action order:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to update action order' });
        setMeeting({ ...meeting, actions: previousActions });
      }
    }
  };

  const handleCopyAgenda = async () => {
    if (!meeting || !meeting.agenda) return;

    try {
      // Build HTML content
      let htmlContent = '';
      let plainTextContent = '';

      const agendaTitle = `${meeting.title} - Agenda ${format(new Date(meeting.date), 'dd/MM/yyyy')}`;
      htmlContent += `<h2>${agendaTitle}</h2>\n<ul>\n`;
      plainTextContent += `${agendaTitle}\n`;

      const sortedAgenda = [...meeting.agenda].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      
      if (sortedAgenda.length === 0) {
        htmlContent += `  <li><em>No agenda items</em></li>\n`;
        plainTextContent += `• No agenda items\n`;
      } else {
        sortedAgenda.forEach(item => {
          if (item.completed) {
            htmlContent += `  <li><s>${item.text}</s></li>\n`;
            plainTextContent += `• ${item.text} (completed)\n`;
          } else {
            htmlContent += `  <li>${item.text}</li>\n`;
            plainTextContent += `• ${item.text}\n`;
          }
        });
      }
      
      htmlContent += `</ul>\n`;

      // Copy to clipboard with both HTML and plain text
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainTextContent], { type: 'text/plain' })
      });

      await navigator.clipboard.write([clipboardItem]);
      toast({ title: 'Copied!', description: 'Agenda copied to clipboard' });
    } catch (err) {
      console.error('Error copying agenda:', err);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to copy agenda to clipboard' });
    }
  };

  const handleCopyActions = async () => {
    if (!meeting || !meeting.actions) return;

    try {
      // Fetch previous actions
      const previousActionsData = await getPreviousActions(meeting.id, showCompletedPrevious);
      const previousMeetings = previousActionsData.meetings || [];

      // Helper function to remove hashtags from text
      const removeHashtags = (text: string) => text.replace(/#\w+/g, '').trim();

      // Build HTML content
      let htmlContent = '';
      let plainTextContent = '';

      // Current meeting - always show all actions
      const currentMeetingTitle = `${meeting.title} - Actions ${format(new Date(meeting.date), 'dd/MM/yyyy')}`;
      htmlContent += `<h2>${currentMeetingTitle}</h2>\n<ul>\n`;
      plainTextContent += `${currentMeetingTitle}\n`;

      const sortedCurrentActions = [...meeting.actions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      
      if (sortedCurrentActions.length === 0) {
        // No actions for current meeting
        htmlContent += `  <li><em>No actions recorded</em></li>\n`;
        plainTextContent += `• No actions recorded\n`;
      } else {
        sortedCurrentActions.forEach(action => {
          const text = removeHashtags(action.text);
          if (action.completed) {
            htmlContent += `  <li><s>${text}</s></li>\n`;
            plainTextContent += `• ${text} (completed)\n`;
          } else {
            htmlContent += `  <li>${text}</li>\n`;
            plainTextContent += `• ${text}\n`;
          }
        });
      }
      
      htmlContent += `</ul>\n`;
      plainTextContent += `\n`;

      // Previous meetings - respect showCompletedPrevious toggle
      previousMeetings.forEach((prevMeeting: any) => {
        const prevMeetingTitle = `${prevMeeting.title} - ${format(new Date(prevMeeting.date), 'dd/MM/yyyy')}`;
        htmlContent += `<h2>${prevMeetingTitle}</h2>\n<ul>\n`;
        plainTextContent += `${prevMeetingTitle}\n`;

        const sortedActions = [...prevMeeting.actions].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
        sortedActions.forEach((action: any) => {
          const text = removeHashtags(action.text);
          if (action.completed) {
            htmlContent += `  <li><s>${text}</s></li>\n`;
            plainTextContent += `• ${text} (completed)\n`;
          } else {
            htmlContent += `  <li>${text}</li>\n`;
            plainTextContent += `• ${text}\n`;
          }
        });
        htmlContent += `</ul>\n`;
        plainTextContent += `\n`;
      });

      // Copy to clipboard with both HTML and plain text
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainTextContent], { type: 'text/plain' })
      });

      await navigator.clipboard.write([clipboardItem]);
      toast({ title: 'Copied!', description: 'Actions copied to clipboard' });
    } catch (err) {
      console.error('Error copying actions:', err);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to copy actions to clipboard' });
    }
  };

  const timeOptions = Array.from({ length: 96 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center text-destructive">{error}</div>;
  if (!meeting) return <div className="text-center">Meeting not found</div>;

  return (
    <div className="space-y-8">
      {meeting && (
        <>
          <div className="space-y-4">
            {/* Title with buttons to the right on desktop only */}
            <div className="md:flex md:justify-between md:items-center md:gap-3">
              {/* Back button + Title */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/app/meetings')}
                  className="glass-panel p-2.5 hover:bg-white/50 dark:hover:bg-slate-700/50 flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                  </svg>
                </Button>
                
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  className="text-3xl font-bold h-auto text-foreground bg-transparent border-transparent focus:border-transparent focus:ring-0 px-0 lg:text-4xl"
                  placeholder="Enter meeting title..."
                />
              </div>
              
              <div className="hidden md:flex md:items-center md:gap-2 md:mt-0">
                {/* View Toggle */}
                <div className="flex bg-muted/60 p-1 rounded-lg border border-border backdrop-blur-sm">
                  <button
                    onClick={onToggleLayout}
                    className={`p-1.5 rounded-md transition-all ${
                      layout === 'classic'
                        ? 'bg-background text-primary shadow-sm'
                        : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
                    }`}
                    title="Classic view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onToggleLayout}
                    className={`p-1.5 rounded-md transition-all ${
                      layout === 'card'
                        ? 'bg-background text-primary shadow-sm'
                        : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
                    }`}
                    title="Card view"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>

                <Button variant="outline" className="w-[190px]" asChild>
                  <DuplicateMeeting
                    meetingId={meeting.id} 
                    meetingTitle={meeting.title}
                    onDuplicate={handleDuplicate}
                  />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-[120px] hover:bg-gradient-to-r hover:from-red-600 hover:to-red-700 hover:text-white hover:border-transparent transition-all duration-300">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Meeting</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this meeting? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteMeeting} className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/40">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            
            {/* Buttons below title on mobile only */}
            <div className="flex items-center gap-2 md:hidden">
              <Button variant="outline" className="flex-1" asChild>
                <DuplicateMeeting
                  meetingId={meeting.id} 
                  meetingTitle={meeting.title}
                  onDuplicate={handleDuplicate}
                />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="flex-1 hover:bg-gradient-to-r hover:from-red-600 hover:to-red-700 hover:text-white hover:border-transparent transition-all duration-300">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Meeting</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this meeting? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteMeeting} className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/40">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            
            {/* Date/time controls */}
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn(
                      "justify-start text-left font-normal",
                      !date && "text-muted-foreground",
                      "flex-1 md:flex-none md:w-[240px]"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar 
                    mode="single" 
                    selected={date} 
                    onSelect={(newDate) => handleDateTimeChange(newDate, time)} 
                    initialFocus 
                  />
                </PopoverContent>
              </Popover>
  
              <Select value={time} onValueChange={(newTime) => handleDateTimeChange(date, newTime)}>
                <SelectTrigger className="flex-1 md:flex-none md:w-[120px]">
                  <Clock className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.map((timeOption) => (
                    <SelectItem key={timeOption} value={timeOption}>
                      {timeOption}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
  
          <div className="space-y-2">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Shared with</h2>
            <EmailShare emails={meeting.sharedWith} onAddEmail={handleAddSharedUser} onRemoveEmail={handleRemoveSharedUser} />
          </div>
  
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Agenda</h2>
              <Button size="sm" onClick={handleCopyAgenda} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white border-transparent shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40">
                <Copy className="h-4 w-4 mr-2" />
                Copy agenda
              </Button>
            </div>
            <AgendaList
              agenda={meeting.agenda}
              onAddAgendaItem={handleAddAgendaItem}
              onDeleteAgendaItem={handleDeleteAgendaItem}
              onReorderAgenda={handleReorderAgenda}
              onUpdateAgendaItem={handleUpdateAgendaItem}
              variant="classic"
            />
          </div>
  
          <div className="space-y-2">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Notes</h2>
            <MeetingEditor 
              key={meeting.id}
              meetingId={meeting.id}
              defaultValue={meeting.notes} 
              onChange={handleNotesChange} 
            />
          </div>
  
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Actions</h2>
              <Button size="sm" onClick={handleCopyActions} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white border-transparent shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40">
                <Copy className="h-4 w-4 mr-2" />
                Copy actions
              </Button>
            </div>
            <ActionList
              actions={meeting.actions}
              onAddActionItem={handleAddActionItem}
              onDeleteActionItem={handleDeleteActionItem}
              onReorderAction={handleReorderAction}
              onUpdateActionItem={handleUpdateActionItem}
              variant="classic"
            />
          </div>
  
          <PreviousActionList 
            currentMeetingId={meeting.id}
            showCompleted={showCompletedPrevious}
            onShowCompletedChange={setShowCompletedPrevious}
            showTitle={true}
          />
        </>
      )}
    </div>
  );
}
